import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NryfService } from '../../@shared/services/nryf.service';
import { GeocodingService } from '../../@shared/services/geocoding.service';
import {
  NryfEventResponse,
  NryfMasterdataResponse,
  NryfCompetitionResponse
} from '../../@shared/models/response.module';
import { MapMarker, MapConfig } from '../../@shared/components/location-map/location-map';

@Component({
  selector: 'app-nryf-event-details',
  standalone: false,
  templateUrl: './nryf-event-details.html',
  styleUrl: './nryf-event-details.css'
})
export class NryfEventDetails implements OnInit {
  eventId: string = '';
  event: NryfEventResponse | null = null;
  masterdata: NryfMasterdataResponse | null = null;
  loading = true;

  // Map configuration
  mapMarkers: MapMarker[] = [];
  mapConfig: MapConfig = {
    height: '250px',
    mobileHeight: '200px',
    enableClustering: false,
    defaultZoom: 14,
    directionsMode: true
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private nryfService: NryfService,
    private geocodingService: GeocodingService
  ) {}

  ngOnInit() {
    this.eventId = this.route.snapshot.paramMap.get('id') || '';
    if (this.eventId) {
      this.loadEvent();
    }
  }

  async loadEvent() {
    this.loading = true;
    try {
      // Load masterdata first for discipline/region names
      this.nryfService.getMasterdata().subscribe({
        next: (masterdataRes) => {
          this.masterdata = masterdataRes?.data || null;
        }
      });

      // Load event details
      this.nryfService.getEventDetails(this.eventId).subscribe({
        next: async (eventRes) => {
          this.event = eventRes?.data || null;

          // Geocode address instead of using potentially incorrect coordinates
          if (this.event) {
            await this.geocodeEventLocation();
          }

          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
    } catch {
      this.loading = false;
    }
  }

  private async geocodeEventLocation() {
    if (!this.event) return;

    const addressParts = [this.event.place, this.event.venueAddress].filter(Boolean);
    if (addressParts.length === 0) return;

    const address = addressParts.join(', ');
    const result = await this.geocodingService.geocodeAddress(address, '', '', 'Norway');

    if (result.success && result.lat && result.lng) {
      this.mapMarkers = [{
        id: this.event.id,
        lat: result.lat,
        lng: result.lng,
        title: this.event.name,
        subtitle: this.event.place || this.event.venueAddress || '',
        data: this.event
      }];
    }
  }

  get registrationUrl(): string {
    return this.nryfService.buildRegistrationUrl(this.eventId);
  }

  getDisciplineName(): string {
    if (!this.event?.disciplineId) return '';
    return this.nryfService.getDisciplineName(this.event.disciplineId);
  }

  getRegionName(): string {
    if (!this.event?.regionId) return '';
    return this.nryfService.getRegionName(this.event.regionId);
  }

  getCompetitionLevelName(levelId: string): string {
    return this.nryfService.getCompetitionLevelName(levelId);
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  formatShortDate(dateString: string | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'short'
    });
  }

  formatDateRange(): string {
    if (!this.event?.startDate) return '';
    const start = this.formatDate(this.event.startDate);
    if (!this.event.endDate || this.event.endDate === this.event.startDate) {
      return start;
    }
    return `${start} - ${this.formatDate(this.event.endDate)}`;
  }

  formatPrice(cents: number | undefined): string {
    if (!cents) return '';
    const price = cents / 100;
    return `${price.toLocaleString('nb-NO')} NOK`;
  }

  getGoogleMapsUrl(): string {
    if (!this.event) return '';

    const addressParts = [
      this.event.venueAddress,
      this.event.place
    ].filter(part => part);

    const fullAddress = addressParts.join(', ');
    const encodedAddress = encodeURIComponent(fullAddress);

    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  goBack() {
    this.router.navigate(['/shows']);
  }

  // Group competitions by date
  getCompetitionsByDate(): { date: string; competitions: NryfCompetitionResponse[] }[] {
    if (!this.event?.competitions) return [];

    const grouped = new Map<string, NryfCompetitionResponse[]>();

    for (const comp of this.event.competitions) {
      const dateKey = comp.startDate || 'unscheduled';

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(comp);
    }

    return Array.from(grouped.entries())
      .sort((a, b) => {
        if (a[0] === 'unscheduled') return 1;
        if (b[0] === 'unscheduled') return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([date, competitions]) => ({ date, competitions }));
  }

  formatCompetitionDate(dateKey: string): string {
    if (dateKey === 'unscheduled') {
      return 'Tidspunkt ikke annonsert';
    }
    const date = new Date(dateKey);
    return date.toLocaleDateString('nb-NO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  }

  // Sort info fields in a logical order for users
  getSortedInfoFields(): { id: string; categoryName: string; text: string }[] {
    if (!this.event?.infoFields?.length) return [];

    // Define priority order (lower number = higher priority)
    const priorityOrder: { [key: string]: number } = {
      'EGENDEFINERT INFORMASJON': 1,
      'OPPSTALLING OG BANEFORHOLD': 2,
      'PREMIERING': 3,
      'AVGIFTER OG BETALING': 4,
      'BETALING': 5,
      'FRISTER': 6,
      'PÅMELDING': 7,
      'PREMIER': 8,
      'KLASSER': 9,
      'TIDSSKJEMA': 10,
      'PROGRAM': 11,
      'STALL OG OPPSTALLING': 12,
      'OPPSTALLING': 13,
      'PRAKTISK INFORMASJON': 14,
      'PRAKTISK INFO': 15,
      'STEVNEINFORMASJON': 16,
      'DOMMERE': 17,
      'VETERINÆR': 18,
      'KONTAKT': 19,
      'ANNET': 99
    };

    return [...this.event.infoFields].sort((a, b) => {
      const priorityA = priorityOrder[a.categoryName.toUpperCase()] || 50;
      const priorityB = priorityOrder[b.categoryName.toUpperCase()] || 50;
      return priorityA - priorityB;
    });
  }
}
